import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { RANK_FRAMES, RANK_TIERS, type FrameSpec, type RankTier } from '@shared/types';
import { api, resolveAssetUrl, type AdminCosmetic, type Rarity } from '@/api/client';
import { ErrorBox, Field, Loading, Modal, PageTitle } from '@/components/ui';

type RankFrameMap = Record<string, FrameSpec>;

const RARITY_TINT: Record<Rarity, string> = {
  common: 'var(--ink-muted)',
  rare: 'var(--c-blue)',
  epic: 'var(--c-purple)',
  legendary: 'var(--c-yellow)',
};

/** Mirrors the client Avatar frame render. An `imageUrl` overlay wins;
 * otherwise the CSS `border` (color/gradient) + `glow` ring is shown. */
function FrameRing({
  border,
  glow,
  imageUrl,
  size = 56,
}: {
  border?: string | null;
  glow?: string | null;
  imageUrl?: string | null;
  size?: number;
}) {
  const img = resolveAssetUrl(imageUrl);
  const avatar = (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'radial-gradient(circle at 50% 30%, #3a1a3a, #1E1B4B)',
      }}
    />
  );

  if (img) {
    const pad = Math.round(size * 0.2);
    const outer = size + pad * 2;
    return (
      <div
        style={{
          position: 'relative',
          width: outer,
          height: outer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {avatar}
        <img
          src={img}
          alt=""
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </div>
    );
  }

  const bw = Math.max(2, Math.round(size * 0.06));
  return (
    <div
      style={{
        width: size + bw * 2,
        height: size + bw * 2,
        borderRadius: '50%',
        padding: bw,
        boxSizing: 'border-box',
        background: border || 'var(--hairline)',
        boxShadow: glow ? `0 0 14px ${glow}, 0 0 4px ${glow}` : 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {avatar}
    </div>
  );
}

function isBossFrame(frame: AdminCosmetic): boolean {
  return frame.id.startsWith('boss-frame-') || frame.data?.source === 'boss';
}

function FramesTable({
  items,
  kind,
  emptyText,
  onEdit,
}: {
  items: AdminCosmetic[];
  kind: 'shop' | 'boss' | 'other';
  emptyText: string;
  onEdit: (item: AdminCosmetic) => void;
}) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <table className="data">
        <thead>
          <tr>
            <th style={{ width: 80 }}>Preview</th>
            <th>Name</th>
            <th>Rarity</th>
            <th style={{ textAlign: 'right' }}>Coins</th>
            <th style={{ textAlign: 'right' }}>Gems</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => {
            const border = (c.data?.border as string | undefined) ?? null;
            const glow = (c.data?.glow as string | undefined) ?? null;
            const imageUrl = (c.data?.imageUrl as string | undefined) ?? null;
            return (
              <tr key={c.id} onClick={() => onEdit(c)}>
                <td>
                  <FrameRing border={border} glow={glow} imageUrl={imageUrl} size={36} />
                </td>
                <td>
                  <div style={{ fontWeight: 600 }}>{c.name}</div>
                  {c.description && (
                    <div style={{ color: 'var(--ink-muted)', fontSize: 11, marginTop: 2 }}>
                      {c.description}
                    </div>
                  )}
                </td>
                <td>
                  <span className="tag" style={{ color: RARITY_TINT[c.rarity], fontSize: 9 }}>
                    {c.rarity.toUpperCase()}
                  </span>
                </td>
                <td className="mono" style={{ textAlign: 'right' }}>
                  {kind === 'shop' ? c.priceCoins.toLocaleString() : '—'}
                </td>
                <td className="mono" style={{ textAlign: 'right' }}>
                  {kind === 'shop' ? c.priceGems.toLocaleString() : '—'}
                </td>
                <td>
                  {kind === 'boss' && (
                    <>
                      <span className="badge badge-guest" style={{ marginRight: 4 }}>BOSS</span>
                      <span className="badge badge-ban">NOT FOR SALE</span>
                    </>
                  )}
                  {kind === 'other' && <span className="badge badge-ban">HIDDEN / BUNDLE</span>}
                  {kind === 'shop' && c.isStarter && (
                    <span className="badge badge-guest" style={{ marginRight: 4 }}>STARTER</span>
                  )}
                </td>
              </tr>
            );
          })}
          {items.length === 0 && (
            <tr style={{ cursor: 'default' }}>
              <td colSpan={6} style={{ textAlign: 'center', color: 'var(--ink-muted)' }}>
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function FramesPage() {
  const [items, setItems] = useState<AdminCosmetic[] | null>(null);
  const [overrides, setOverrides] = useState<RankFrameMap>({});
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminCosmetic | 'new' | null>(null);
  const [editingRank, setEditingRank] = useState<RankTier | null>(null);

  const load = () =>
    Promise.all([api.cosmetics(), api.rankFrames()])
      .then(([cos, rf]) => {
        setItems(cos.items.filter((c) => c.slot === 'avatarFrame'));
        setOverrides(rf.overrides ?? {});
      })
      .catch((e) => setErr(String(e)));

  useEffect(() => {
    void load();
  }, []);

  if (err) return <ErrorBox text={err} />;
  if (!items) return <Loading />;

  const bossFrames = items.filter(isBossFrame);
  const shopFrames = items.filter((item) => !isBossFrame(item) && item.isActive);
  const otherFrames = items.filter((item) => !isBossFrame(item) && !item.isActive);

  return (
    <div>
      <PageTitle
        title="Avatar Frames"
        action={
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            + NEW FRAME
          </button>
        }
      />

      {/* Rank-tier default frames — shown when a player has no frame equipped;
          they upgrade automatically on rank up/down. Each tier can be overridden
          here with an uploaded image (or custom colors). */}
      <div className="tag" style={{ marginBottom: 8 }}>RANK DEFAULTS (click to change image)</div>
      <div className="card" style={{ padding: 14, marginBottom: 18 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
            gap: 12,
          }}
        >
          {RANK_TIERS.map((tier: RankTier) => {
            const ov = overrides[tier];
            const f = ov ?? RANK_FRAMES[tier];
            return (
              <button
                key={tier}
                onClick={() => setEditingRank(tier)}
                title="Click to change this rank's frame"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  background: 'transparent',
                  border: '1px solid var(--hairline)',
                  borderRadius: 12,
                  padding: '12px 6px',
                  cursor: 'pointer',
                }}
              >
                <FrameRing border={f.border} glow={f.glow} imageUrl={f.imageUrl} size={48} />
                <div className="display" style={{ fontSize: 11, letterSpacing: '0.08em' }}>
                  {tier.toUpperCase()}
                </div>
                {ov ? (
                  <span className="badge badge-guest" style={{ fontSize: 8 }}>CUSTOM</span>
                ) : (
                  <span className="tag" style={{ fontSize: 8, color: 'var(--ink-dim)' }}>DEFAULT</span>
                )}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-dim)', marginTop: 12 }}>
          Every player gets these by rank when no purchased frame is equipped.
          Upload an image to replace a tier; “Reset” restores the built-in default.
        </div>
      </div>

      <div className="tag" style={{ marginBottom: 8 }}>BOSS REWARD FRAMES (not purchasable)</div>
      <FramesTable
        items={bossFrames}
        kind="boss"
        emptyText="No Boss reward frames."
        onEdit={setEditing}
      />

      <div className="tag" style={{ margin: '18px 0 8px' }}>SHOP FRAMES (purchasable)</div>
      <FramesTable
        items={shopFrames}
        kind="shop"
        emptyText="No active shop frames. Click “+ NEW FRAME” to add one."
        onEdit={setEditing}
      />

      {otherFrames.length > 0 && (
        <>
          <div className="tag" style={{ margin: '18px 0 8px' }}>BUNDLE / HIDDEN FRAMES</div>
          <FramesTable
            items={otherFrames}
            kind="other"
            emptyText="No hidden frames."
            onEdit={setEditing}
          />
        </>
      )}

      {editing && (
        <FrameEdit
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
          onDeleted={() => {
            setEditing(null);
            void load();
          }}
        />
      )}

      {editingRank && (
        <RankFrameEdit
          tier={editingRank}
          override={overrides[editingRank] ?? null}
          onClose={() => setEditingRank(null)}
          onDone={() => {
            setEditingRank(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

/** Edit a single rank-tier default frame: upload an image override or tweak the
 * fallback colors. "Reset" deletes the override and restores the code default. */
function RankFrameEdit({
  tier,
  override,
  onClose,
  onDone,
}: {
  tier: RankTier;
  override: FrameSpec | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const base = RANK_FRAMES[tier];
  const seed = override ?? base;
  const [border, setBorder] = useState(seed.border ?? '');
  const [glow, setGlow] = useState(seed.glow ?? '');
  const [imageUrl, setImageUrl] = useState(seed.imageUrl ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.setRankFrame(tier, {
        border: border || null,
        glow: glow || null,
        imageUrl: imageUrl || null,
      });
      onDone();
    } catch (e) {
      setErr(String(e));
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!confirm(`Reset ${tier.toUpperCase()} frame to the built-in default?`)) return;
    setBusy(true);
    try {
      await api.resetRankFrame(tier);
      onDone();
    } catch (e) {
      setErr(String(e));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Rank frame · ${tier.toUpperCase()}`}
      onClose={onClose}
      footer={
        <>
          {override && (
            <button className="btn btn-danger" onClick={reset} disabled={busy}>
              Reset to default
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 8px' }}>
        <FrameRing border={border} glow={glow} imageUrl={imageUrl} size={72} />
      </div>

      <FrameImageUploader imageUrl={imageUrl} onChange={setImageUrl} />

      <div style={{ fontSize: 11, color: 'var(--ink-dim)' }}>
        With an image set, it’s used as the frame. The colors below are the
        fallback ring used only when no image is uploaded.
      </div>
      <Field
        label="BORDER (color or CSS gradient)"
        hint='e.g. #F4C724 or linear-gradient(135deg, #F4C724, #a07c00)'
      >
        <ColorOrText value={border} onChange={setBorder} placeholder="#F4C724 or gradient(…)" />
      </Field>
      <Field label="GLOW (color)" hint="Outer glow color, e.g. #22D3EE or rgba(34,211,238,0.6)">
        <ColorOrText value={glow} onChange={setGlow} placeholder="#22D3EE" />
      </Field>

      {err && <ErrorBox text={err} />}
    </Modal>
  );
}

function FrameEdit({
  initial,
  onClose,
  onSaved,
  onDeleted,
}: {
  initial: AdminCosmetic | null;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const bossReward = initial ? isBossFrame(initial) : false;
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [rarity, setRarity] = useState<Rarity>(initial?.rarity ?? 'rare');
  const [priceCoins, setPriceCoins] = useState(initial?.priceCoins ?? 0);
  const [priceGems, setPriceGems] = useState(initial?.priceGems ?? 0);
  const [border, setBorder] = useState((initial?.data?.border as string | undefined) ?? '#F4C724');
  const [glow, setGlow] = useState((initial?.data?.glow as string | undefined) ?? '#F4C724');
  const [imageUrl, setImageUrl] = useState((initial?.data?.imageUrl as string | undefined) ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!name.trim()) {
      setErr('Name is required');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        slot: 'avatarFrame',
        name: name.trim(),
        description: description || null,
        rarity,
        priceCoins: bossReward ? 0 : priceCoins,
        priceGems: bossReward ? 0 : priceGems,
        iconUrl: imageUrl || null,
        data: {
          ...(initial?.data ?? {}),
          border: border || null,
          glow: glow || null,
          imageUrl: imageUrl || null,
          ...(bossReward ? { source: 'boss', rewardOnly: true } : {}),
        },
        isActive: bossReward ? false : isActive,
        // Frames are never starters — the rank default fills that role.
        isStarter: false,
      } as const;
      if (initial) await api.patchCosmetic(initial.id, payload);
      else await api.createCosmetic(payload);
      onSaved();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!initial) return;
    if (!confirm(`Delete frame "${initial.name}"?`)) return;
    setBusy(true);
    try {
      await api.deleteCosmetic(initial.id);
      onDeleted();
    } catch (e) {
      setErr(String(e));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={initial ? `Edit · ${initial.name}` : 'New frame'}
      onClose={onClose}
      footer={
        <>
          {initial && !bossReward && (
            <button className="btn btn-danger" onClick={remove} disabled={busy}>
              Delete
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : initial ? 'Save' : 'Create'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 8px' }}>
        <FrameRing border={border} glow={glow} imageUrl={imageUrl} size={72} />
      </div>

      {bossReward && (
        <div
          className="card"
          style={{ padding: 10, color: 'var(--c-cyan)', fontSize: 12 }}
        >
          BOSS REWARD · NOT FOR SALE<br />
          <span style={{ color: 'var(--ink-muted)' }}>
            Granted only by Boss rewards. Shop status and prices are locked.
          </span>
        </div>
      )}

      <Field label="NAME">
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="RARITY">
        <select value={rarity} onChange={(e) => setRarity(e.target.value as Rarity)}>
          <option value="common">common</option>
          <option value="rare">rare</option>
          <option value="epic">epic</option>
          <option value="legendary">legendary</option>
        </select>
      </Field>
      <Field label="DESCRIPTION">
        <input value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>

      <FrameImageUploader imageUrl={imageUrl} onChange={setImageUrl} />

      <div style={{ fontSize: 11, color: 'var(--ink-dim)' }}>
        With an image set, it’s used as the frame. The colors below are the
        fallback ring used only when no image is uploaded.
      </div>
      <Field
        label="BORDER (color or CSS gradient)"
        hint='e.g. #F4C724 or linear-gradient(135deg, #F4C724, #a07c00)'
      >
        <ColorOrText value={border} onChange={setBorder} placeholder="#F4C724 or gradient(…)" />
      </Field>
      <Field label="GLOW (color)" hint="Outer glow color, e.g. #22D3EE or rgba(34,211,238,0.6)">
        <ColorOrText value={glow} onChange={setGlow} placeholder="#22D3EE" />
      </Field>

      {!bossReward && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="PRICE (COINS)">
              <input type="number" value={priceCoins} onChange={(e) => setPriceCoins(+e.target.value || 0)} />
            </Field>
            <Field label="PRICE (GEMS)">
              <input type="number" value={priceGems} onChange={(e) => setPriceGems(+e.target.value || 0)} />
            </Field>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Active in shop
          </label>
        </>
      )}

      {err && <ErrorBox text={err} />}
    </Modal>
  );
}

/** Upload a transparent PNG ring overlay for the frame. Reuses the cosmetic
 * icon upload endpoint; the returned URL is stored in `data.imageUrl`. */
function FrameImageUploader({
  imageUrl,
  onChange,
}: {
  imageUrl: string;
  onChange: (url: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const preview = resolveAssetUrl(imageUrl);

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null);
    setBusy(true);
    try {
      const { url } = await api.uploadCosmeticIcon(file);
      onChange(url);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Field
      label="FRAME IMAGE"
      hint="Transparent PNG ring overlay · max 5 MB · square, with a hollow center where the avatar shows through."
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 10,
            background:
              'repeating-conic-gradient(rgba(255,255,255,0.04) 0% 25%, transparent 0% 50%) 50% / 12px 12px, rgba(0,0,0,0.35)',
            border: '1px solid var(--hairline)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          {preview ? (
            <img
              src={preview}
              alt="frame preview"
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              onError={() => setErr('Failed to load preview')}
            />
          ) : (
            <span style={{ color: 'var(--ink-dim)', fontSize: 9, textAlign: 'center' }}>NO<br />IMAGE</span>
          )}
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/webp,image/svg+xml"
            onChange={onPick}
            disabled={busy}
            style={{ fontSize: 11 }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={imageUrl}
              onChange={(e) => onChange(e.target.value)}
              placeholder="/uploads/cosmetics/… or external URL"
              style={{ width: '100%', fontSize: 11, fontFamily: 'var(--f-mono)' }}
            />
            {imageUrl && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => onChange('')}
                style={{ fontSize: 10, padding: '4px 8px' }}
                title="Clear image"
              >
                CLEAR
              </button>
            )}
          </div>
          {busy && <span style={{ fontSize: 11, color: 'var(--c-cyan)' }}>Uploading…</span>}
          {err && <ErrorBox text={err} />}
        </div>
      </div>
    </Field>
  );
}

/** Text input with an adjacent native color picker. The picker only works for
 * plain hex colors; gradients are typed into the text box directly. */
function ColorOrText({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const isHex = useMemo(() => /^#[0-9a-fA-F]{6}$/.test(value.trim()), [value]);
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input
        type="color"
        value={isHex ? value.trim() : '#ffffff'}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 40, padding: 0, border: '1px solid var(--hairline)', borderRadius: 6, flexShrink: 0 }}
        title={isHex ? 'Pick color' : 'Picker works with hex colors only'}
      />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width: '100%', fontFamily: 'var(--f-mono)', fontSize: 12 }}
      />
    </div>
  );
}
